import {Injectable, OnDestroy} from '@angular/core';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {Guid} from 'guid-typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Subject} from 'rxjs';
import {Activity, ActivityData} from './activity';
import {ActivityApiService} from './activity-api.service';
import {Group, GroupData} from '@docma-mm-core/group/group';
import {Project} from '@docma-mm-core/project/project';
import {ActivityEditData} from '@docma-mm-core/activity/activity-edit';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';


@UntilDestroy()
@Injectable()
export class ActivityService implements OnDestroy {

  private user: User;

  private activityDeletedSubject = new Subject<{activity: Activity, groupDelete: boolean, modifiedOn: Date, modifier: User}>();
  onActivityDeleted =  this.activityDeletedSubject.asObservable();

  constructor(private readonly activityApiService: ActivityApiService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly projectLoaderService: MmProjectLoaderService) {

    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(u => this.user = u.data.User);
  }

  ngOnDestroy(): void {
  }

  private createNew(activityDate?: Date): {activity: Activity, changeData: ActivityData, createDate: Date} {
    const createDate = (activityDate ?? new Date(Date.now()));
    // console.log('##### createNew.createDate ##### ' + createDate.getTime().toString())

    const activity = new Activity();
    activity.loadState = new LoadState({ status: LoadStatusEnum.Loaded, data: {}, date: createDate });
    activity.syncState = new SyncState({ status: SyncStatusEnum.NotSync, date: createDate });
    const changeData = new ActivityData();
    changeData.UniqueId = Guid.create().toString();
    changeData.SUid =  Guid.create().toString();
    changeData.Date = createDate;
    if (this.user) {
      changeData.Creator = {Account: this.user.Account, FirstName: this.user.FirstName, LastName: this.user.LastName };
      changeData.Modifier = changeData.Creator;
    }
    changeData.CreatedOn = changeData.CreatedOn ?? createDate;
    changeData.ModifiedOn = changeData.ModifiedOn ?? createDate;
    return {activity: activity, changeData: changeData, createDate: createDate};
  }

  createNewActivity(): Activity {
    let newActivity = this.createNew();
    newActivity.activity.changeState = new ChangeState({ data: newActivity.changeData, status: ChangeStatusEnum.New, date: newActivity.createDate });
    return newActivity.activity;
  }

  createNewGroupActivity(group: Group, groupData: GroupData, activityDate: Date): Activity {
    let newActivity = this.createNew(activityDate);
    newActivity.activity.group = group;
    newActivity.changeData.Group = groupData;

    newActivity.activity.changeState = new ChangeState({ data: newActivity.changeData, status: ChangeStatusEnum.New, date: newActivity.createDate });
    return newActivity.activity;
  }

  async deleteActivity(activity: Activity, myDocmaUid: string, groupDelete: boolean = false) {
    const modifiedOn = await this.activityApiService.deleteActivity(activity.id, groupDelete).toPromise();

    if(activity.data.Group?.Id) {
      await this.persistenceService.removePersitedActivites(activity, myDocmaUid, groupDelete, modifiedOn);
    }

    this.activityDeletedSubject.next({activity, groupDelete, modifiedOn, modifier: this.user});

    return modifiedOn;
  }

  async getAffectedOfPatch(currentProject: Project, activityEdit: ActivityEditData) {
    return await this.activityApiService.getAffectedActivities({mydocmaUid: currentProject.MyDocmaUid, projectId: currentProject.Id}, activityEdit).toPromise();
  }


}
